import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Check, Upload } from "lucide-react";
import {
  getMyInvestor,
  updateMyInvestor,
  checkHandleAvailability,
  uploadInvestorLogo,
  getMyLogoUrl,
} from "@/lib/investor.functions";
import { handleReasonMessage, normalizeHandle } from "@/lib/investor-handle";

export const Route = createFileRoute("/investor/share")({
  head: () => ({
    meta: [
      { title: "Share kit - Investor - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ShareKit,
});

type Investor = {
  id: string;
  name: string;
  handle: string | null;
  ref_code: string | null;
  invite_blurb: string | null;
  logo_url: string | null;
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => {
      const s = r.result as string;
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.readAsDataURL(file);
  });
}

function ShareKit() {
  const runGet = useServerFn(getMyInvestor);
  const runUpdate = useServerFn(updateMyInvestor);
  const runCheck = useServerFn(checkHandleAvailability);
  const runUpload = useServerFn(uploadInvestorLogo);
  const runLogoUrl = useServerFn(getMyLogoUrl);

  const [loading, setLoading] = useState(true);
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [handle, setHandle] = useState("");
  const [handleStatus, setHandleStatus] = useState<
    { kind: "idle" } | { kind: "checking" } | { kind: "ok" } | { kind: "err"; msg: string }
  >({ kind: "idle" });
  const [blurb, setBlurb] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { investor } = await runGet();
        if (investor) {
          setInvestor(investor as Investor);
          setHandle((investor.handle ?? "") as string);
          setBlurb((investor.invite_blurb ?? "") as string);
        }
        const { url } = await runLogoUrl();
        setLogoUrl(url);
      } finally {
        setLoading(false);
      }
    })();
  }, [runGet, runLogoUrl]);

  // Live availability check (debounced).
  useEffect(() => {
    const raw = normalizeHandle(handle);
    if (!raw || raw === (investor?.handle ?? "")) {
      setHandleStatus({ kind: "idle" });
      return;
    }
    setHandleStatus({ kind: "checking" });
    const t = setTimeout(async () => {
      try {
        const res = await runCheck({ data: { handle: raw } });
        if (res.available) setHandleStatus({ kind: "ok" });
        else if (res.reason === "taken") setHandleStatus({ kind: "err", msg: "That handle is already taken." });
        else setHandleStatus({ kind: "err", msg: handleReasonMessage(res.reason as Exclude<typeof res.reason, "ok" | "taken">) });
      } catch (err) {
        setHandleStatus({ kind: "err", msg: err instanceof Error ? err.message : "Check failed" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [handle, investor?.handle, runCheck]);

  const brandedLink = useMemo(() => {
    const h = normalizeHandle(handle) || investor?.handle || "";
    return h ? `https://deckscore.ai/${h}` : "";
  }, [handle, investor?.handle]);

  const linkedInSnippet = useMemo(() => {
    if (!brandedLink) return "";
    const firm = investor?.name || "me";
    return `🚀 Founders: get your pitch deck investor-ready + in front of ${firm} → ${brandedLink}`;
  }, [brandedLink, investor?.name]);

  async function copy(kind: string, text: string) {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* noop */ }
  }

  async function onLogoPick(file: File) {
    setUploading(true);
    try {
      const fileBase64 = await fileToBase64(file);
      await runUpload({
        data: { fileName: file.name, contentBase64: fileBase64, contentType: file.type || "image/png" },
      });
      const { url } = await runLogoUrl();
      setLogoUrl(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function onSave() {
    if (!investor) return;
    setSaving(true); setSaved(false);
    try {
      const payload = {
        name: investor.name,
        handle: normalizeHandle(handle) || null,
        invite_blurb: blurb.trim() || null,
      };
      const res = await runUpdate({ data: payload });
      if (res.investor) {
        setInvestor((prev) => (prev ? { ...prev, ...res.investor } as Investor : prev));
        setSaved(true);
        setTimeout(() => setSaved(false), 1500);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-ink-secondary">Loading…</p>;
  if (!investor) {
    return (
      <div className="bg-surface border border-line rounded-2xl p-10 text-center">
        <h1 className="font-display text-[22px] font-semibold">Register as an investor first</h1>
        <p className="text-ink-secondary mt-2">Then come back to build your share kit.</p>
        <a href="/for-investors" className="inline-flex mt-4 rounded-full bg-accent text-accent-ink px-5 py-2 text-[14px] font-bold">Continue →</a>
      </div>
    );
  }

  const inputCls =
    "min-h-[40px] w-full rounded-lg border border-line bg-bg px-3 text-[14px] text-ink placeholder:text-ink-tertiary focus:border-accent focus-visible:outline-none";

  const currentHandle = normalizeHandle(handle) || investor.handle || "your-handle";

  return (
    <>
      <div className="mb-8">
        <p className="font-script text-accent text-[22px] leading-none">share</p>
        <h1 className="font-display text-[28px] font-semibold mt-1">Share kit</h1>
        <p className="text-ink-secondary text-[14px] mt-1">
          A branded link founders can click straight from your bio.
        </p>
      </div>

      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8 mb-6">
        <h2 className="font-display text-[18px] font-semibold mb-2">Your dealflow page</h2>
        <div className="flex gap-2 items-center">
          <input readOnly value={brandedLink} className={inputCls + " font-mono"} />
          <button
            onClick={() => copy("link", brandedLink)}
            className="inline-flex items-center gap-1 rounded-full bg-accent text-accent-ink px-4 py-2 text-[13px] font-semibold"
          >
            {copied === "link" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied === "link" ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="text-[12.5px] text-ink-tertiary mt-2">
          Founders who arrive here get the exact same free analysis - and you receive their one-pager.
        </p>
      </section>

      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8 mb-6">
        <h2 className="font-display text-[18px] font-semibold mb-1">Preview share card</h2>
        <p className="text-[12.5px] text-ink-tertiary mb-4">
          This is the exact image people see when your link is shared on LinkedIn, X, or in messages.
        </p>
        <div className="rounded-xl overflow-hidden border border-line bg-black">
          <img
            src={`/og/${currentHandle}?t=${investor.id}-${logoUrl ? "1" : "0"}`}
            alt="Share card preview"
            className="w-full block"
          />
        </div>
      </section>

      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8 mb-6">
        <h2 className="font-display text-[18px] font-semibold mb-2">LinkedIn bio snippet</h2>
        <textarea readOnly rows={2} value={linkedInSnippet} className={inputCls + " min-h-[64px] font-mono text-[12.5px]"} />
        <div className="mt-2">
          <button
            onClick={() => copy("snippet", linkedInSnippet)}
            className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 text-[12.5px]"
          >
            {copied === "snippet" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === "snippet" ? "Copied" : "Copy snippet"}
          </button>
        </div>
      </section>

      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8 mb-6">
        <h2 className="font-display text-[18px] font-semibold mb-4">Customize</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-[13px] text-ink-secondary block mb-1">Handle</label>
            <div className="flex items-center gap-2">
              <span className="text-[13.5px] text-ink-tertiary font-mono">deckscore.ai/</span>
              <input
                className={inputCls}
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="your-handle"
                autoCapitalize="none"
                spellCheck={false}
              />
            </div>
            <div className="text-[12px] mt-1 min-h-[16px]">
              {handleStatus.kind === "checking" && <span className="text-ink-tertiary">Checking…</span>}
              {handleStatus.kind === "ok" && <span className="text-[#1F7A3C]">Available ✓</span>}
              {handleStatus.kind === "err" && <span className="text-red-700">{handleStatus.msg}</span>}
              {handleStatus.kind === "idle" && investor.handle && (
                <span className="text-ink-tertiary">Current: {investor.handle}</span>
              )}
            </div>
          </div>

          <div>
            <label className="text-[13px] text-ink-secondary block mb-1">Logo</label>
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-xl border border-line bg-bg overflow-hidden flex items-center justify-center">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-[10px] text-ink-tertiary">No logo</span>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onLogoPick(f);
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-[13px] disabled:opacity-60"
              >
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : logoUrl ? "Replace" : "Upload"}
              </button>
            </div>
            <p className="text-[12px] text-ink-tertiary mt-2">PNG/JPG/SVG, up to 1.5 MB.</p>
          </div>
        </div>

        <div className="mt-6">
          <label className="text-[13px] text-ink-secondary block mb-1">Custom invite line (optional)</label>
          <textarea
            rows={3}
            className={inputCls + " min-h-[80px]"}
            value={blurb}
            maxLength={220}
            onChange={(e) => setBlurb(e.target.value)}
            placeholder="Founders - send me your deck. I'll take a look this week."
          />
          <p className="text-[12px] text-ink-tertiary mt-1">Replaces the default gift line on your branded page.</p>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={onSave}
            disabled={saving || handleStatus.kind === "checking" || handleStatus.kind === "err"}
            className="inline-flex rounded-full bg-accent text-accent-ink px-5 py-2 text-[14px] font-bold disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          {saved && <span className="text-[13px] text-[#1F7A3C]">Saved ✓</span>}
        </div>
      </section>
    </>
  );
}
